import React from 'react';
import Cookies from 'js-cookie';
import { Box, Stack, OutlinedInput, InputAdornment } from '@mui/material';
import { motion } from 'framer-motion';

import EditorialPageShell from '../components/editorial/EditorialPageShell';
import PageHero from '../components/editorial/PageHero';
import EditorialSection from '../components/editorial/EditorialSection';
import EditorialButton from '../components/editorial/EditorialButton';

import SocialLoginModal from '../components/modal/SocialLoginModal';
import { googleAuth, appleAuth } from '../service/socialAuth';
import { updateUserPhone } from '../api/users/saveUsers';
import useFirebaseUser from '../hooks/useFirebaseUser';

const QUICK_AMOUNTS = [20, 50, 150, 250, 500, 1000];

const WAYS = [
  {
    no: '01',
    head: 'In Person',
    body: 'Drop off a check at KBT — 9017 W. Pico Blvd. Los Angeles, CA 90035.',
    cta: { label: 'View on map', href: 'https://maps.app.goo.gl/z5bH726haFUNAfk47', variant: 'ghost' },
  },
  {
    no: '02',
    head: 'Become a Member',
    body: 'Members are the backbone of KBT. Partner with the kehilla through monthly or one-time membership.',
    cta: { label: 'Become a Member', href: '/membership2026', variant: 'secondary' },
  },
  {
    no: '03',
    head: 'Venmo',
    body: '@KehilatBneiTorah  ·  the quickest way to send a gift directly.',
  },
  {
    no: '04',
    head: 'PayPal',
    body: 'Easily contribute via PayPal — every dollar supports the daily life of the kehilla.',
    cta: { label: 'Pay with PayPal', href: '#', variant: 'ghost' },
  },
];

export default function DonateNow() {
  const [open, setOpen] = React.useState(false);
  const [amount, setAmount] = React.useState('');
  const [isClicked, setClicked] = React.useState(false);
  const { user } = useFirebaseUser();
  const uid = user?.uid;

  const handleGoogle = async () => {
    try {
      const res = await googleAuth();
      if (res?.needsLink) return res;
      return res?.uid ? res : null;
    } catch (e) {
      console.log(e);
      return null;
    }
  };
  const handleApple = async () => {
    try {
      const res = await appleAuth();
      if (res?.needsLink) return res;
      return res?.uid ? res : null;
    } catch (e) {
      console.log(e);
      return null;
    }
  };
  const handleAccountCreated = async (res) => {
    if (res?.uid) {
      Cookies.set('uid', res.uid);
      Cookies.set('email', res.email);
      if (res.customerStripeID) Cookies.set('customerStripeID', res.customerStripeID);
      if (res.phoneNumber && res.phoneVerified) {
        try {
          await updateUserPhone(res.uid, res.phoneNumber, true);
          Cookies.set('phoneVerified', 'true');
        } catch (e) {
          console.error(e);
        }
      }
    }
  };

  const continueHref = amount !== '' && uid ? '/payment-checkout' : null;

  return (
    <EditorialPageShell
      title="Donate"
      description="Help spread Torah in Los Angeles. Support Kehilat Bnei Torah with a one-time gift or recurring membership."
    >
      <PageHero
        kicker="Donate  ·  KBT"
        meta="Help spread Torah"
        title="Help us bring a"
        titleAccent="million hours of Torah back to LA."
        subtitle="Every dollar fuels the daily seder, weekly shiurim, and the open table. Pick an amount — or write your own — and the kehilla takes it from there."
        image="/assets/images/kbt/night-seder.jpg"
        height="84vh"
      />

      <EditorialSection
        kicker="One-time gift"
        title="Choose a"
        titleAccent="donation amount."
        intro="Tap an amount to set it instantly, or enter your own. We use Stripe for safe, instant card processing."
        align="center"
        watermark="צ"
      >
        <Box sx={{ maxWidth: 760, mx: 'auto' }}>
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          >
            <Stack
              spacing={4}
              sx={{
                p: { xs: 4, md: 6 },
                background: 'var(--paper)',
                border: '1px solid rgba(36,34,88,0.12)',
                boxShadow: '0 30px 60px -40px rgba(7,6,26,0.25)',
              }}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' },
                  gap: 2,
                }}
              >
                {QUICK_AMOUNTS.map((a) => {
                  const selected = Number(amount) === a;
                  return (
                    <Box
                      key={a}
                      onClick={() => setAmount(a)}
                      sx={{
                        cursor: 'pointer',
                        py: 2.5,
                        textAlign: 'center',
                        border: '1px solid',
                        borderColor: selected ? 'var(--navy-500)' : 'rgba(36,34,88,0.18)',
                        background: selected ? 'var(--navy-500)' : 'transparent',
                        color: selected ? '#fff' : 'var(--navy-500)',
                        fontFamily: 'var(--font-display)',
                        fontWeight: 700,
                        fontSize: 22,
                        letterSpacing: '-0.01em',
                        transition: 'all 250ms ease',
                        '&:hover': { borderColor: 'var(--navy-500)' },
                      }}
                    >
                      ${a}
                    </Box>
                  );
                })}
              </Box>

              <OutlinedInput
                fullWidth
                error={isClicked && amount === ''}
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Other amount"
                startAdornment={
                  <InputAdornment position="start">
                    <Box
                      sx={{
                        fontFamily: 'var(--font-mono)',
                        fontSize: 13,
                        letterSpacing: '0.12em',
                        color: 'var(--navy-300)',
                      }}
                    >
                      $
                    </Box>
                  </InputAdornment>
                }
                endAdornment={
                  <InputAdornment position="end">
                    <Box
                      sx={{
                        fontFamily: 'var(--font-mono)',
                        fontSize: 11,
                        letterSpacing: '0.18em',
                        color: 'var(--navy-300)',
                      }}
                    >
                      USD
                    </Box>
                  </InputAdornment>
                }
                sx={{
                  height: 58,
                  fontFamily: 'var(--font-body)',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(36,34,88,0.18)' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--navy-500)' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'var(--teal-500) !important',
                  },
                }}
              />

              <EditorialButton
                variant="primary"
                fullWidth
                onClick={() => {
                  setClicked(true);
                  localStorage.setItem('amount', amount);
                  localStorage.setItem('paymentType', 'donation');
                  localStorage.setItem('paymentTypeName', 'One Time Donation');
                  if (!uid) setOpen(true);
                }}
                href={continueHref}
              >
                Continue
              </EditorialButton>
            </Stack>
          </motion.div>
        </Box>
      </EditorialSection>

      <EditorialSection
        background="var(--paper-warm)"
        kicker="Other ways to give"
        title="Help grow the LA"
        titleAccent="Jewish community."
        intro="If you'd rather give in person, support the kehilla as a member, or send via Venmo or PayPal — every path lands in the same beit midrash."
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
            gap: { xs: 4, md: 5 },
          }}
        >
          {WAYS.map((w, i) => (
            <motion.div
              key={w.no}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.9, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
            >
              <Box
                sx={{
                  borderTop: '1px solid rgba(36,34,88,0.18)',
                  pt: 4,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2.5,
                  minHeight: 220,
                }}
              >
                <Box
                  sx={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: 11,
                    letterSpacing: '0.22em',
                    color: 'var(--teal-500)',
                  }}
                >
                  {w.no}  /  Way to give
                </Box>
                <Box
                  sx={{
                    fontFamily: 'var(--font-display)',
                    fontWeight: 700,
                    fontSize: { xs: 26, md: 32 },
                    color: 'var(--navy-500)',
                    lineHeight: 1.1,
                    letterSpacing: '-0.01em',
                  }}
                >
                  {w.head}
                </Box>
                <Box
                  sx={{
                    fontFamily: 'var(--font-body)',
                    fontSize: 16,
                    lineHeight: 1.6,
                    color: 'var(--text)',
                    opacity: 0.85,
                    flex: 1,
                  }}
                >
                  {w.body}
                </Box>
                {w.cta && (
                  <Box>
                    <EditorialButton variant={w.cta.variant} href={w.cta.href}>
                      {w.cta.label}
                    </EditorialButton>
                  </Box>
                )}
              </Box>
            </motion.div>
          ))}
        </Box>
      </EditorialSection>

      <SocialLoginModal
        open={open}
        handleClose={() => setOpen(false)}
        googleLogin={handleGoogle}
        appleLogin={handleApple}
        onAccountCreated={handleAccountCreated}
        requirePhoneVerification={true}
      />
    </EditorialPageShell>
  );
}
